import * as React from 'react';
import { isDefined, noop } from '@react-financial-charts/core';
import { getXValue } from '@react-financial-charts/core/lib/utils/ChartDataUtil';
import { saveNodeType } from 'react-financial-charts';
import { HoverTextNearMouse } from '@react-financial-charts/interactive/lib/components/index';
import { InteractiveStraightLine } from './InteractiveStraightLine';
import type { EachTrendLineProps } from './EachTrendLine';
import { ClickableReactangle, ClickableCircle } from '.';
import { FilledArea } from './FilledArea';
import { makeTransparentColor } from '@/utils/handleColorFormat';
import { getNewXY } from '@/utils/utils';
import type { MoreProps } from '@/types/commontypes';
import { fixedAppearance } from '@/utils/defaultShapePropertyValues';

interface EachRectangleState {
  anchor?: string;
  hover?: boolean;
  dragging?: boolean;
}

export class EachRectangle extends React.Component<EachTrendLineProps, EachRectangleState> {
  public static defaultProps = {
    onDrag: noop,
    onEdge1Drag: noop,
    onEdge2Drag: noop,
    onSelect: noop,
    selected: false,
    ...fixedAppearance,
    strokeWidth: 1,
    strokeDasharray: 'Solid',
    hoverText: {
      enable: false,
    },
  };

  private dragStart: { chartId: unknown; x1Value: number; y1Value: number; x2Value: number; y2Value: number } | null =
    null;
  private saveNodeType: <T>(type: string) => React.Ref<T>;

  private lineInteractive: string = 'react-financial-charts-pointer-cursor';

  public constructor(props: EachTrendLineProps) {
    super(props);

    this.saveNodeType = saveNodeType.bind(this);

    this.state = {
      hover: false,
    };
  }

  public render() {
    const {
      x1Value,
      y1Value,
      x2Value,
      y2Value,
      hoverText,
      selected,
      strokeDasharray,
      strokeStyle,
      strokeWidth,
      extendLeft,
      extendRight,
      fillStyle,
      middle,
      dy,
      onDoubleClick,
    } = this.props;

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    const { hover } = this.state;

    const line2Edge =
      x2Value !== undefined && y2Value !== undefined && isDefined(dy) ? (
        <g>
          {this.renderEdge(
            'line2edge1',
            x2Value,
            y2Value,
            'line2edge1',
            this.handleEdge3Drag,
            this.handleEdge3DragStart,
          )}
        </g>
      ) : null;

    const leftXValue = Math.min(x1Value, x2Value);
    const rightXValue = Math.max(x1Value, x2Value);
    const topYValue = Math.min(y1Value, y2Value);
    const bottomYValue = Math.max(y1Value, y2Value);

    return (
      <g>
        {/* Filled Area */}
        <FilledArea
          x1Value={x1Value}
          y1Value={y1Value}
          x2Value={x2Value}
          y2Value={y2Value}
          selected={selected || hover}
          onDragStart={this.handleLineDragStart}
          onDrag={this.handleLineDrag}
          onDragComplete={this.handleDragComplete}
          onMouseDown={this.handleShapeMouseDown}
          strokeStyle={strokeStyle}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          extendLeft={extendLeft}
          extendRight={extendRight}
          fillStyle={fillStyle || 'rgba(156, 39, 176, 0.2)'}
          type="LINE"
          onDoubleClick={onDoubleClick}
        />

        {/* Lines */}
        {this.renderLine('leftLine', leftXValue, topYValue, leftXValue, bottomYValue)}
        {this.renderLine('rightLine', rightXValue, bottomYValue, rightXValue, topYValue)}
        {this.renderLine('topLine', leftXValue, topYValue, rightXValue, topYValue)}
        {this.renderLine('bottomLine', leftXValue, bottomYValue, rightXValue, bottomYValue)}

        {/* Edges */}
        {this.renderEdge('edge1', x1Value, y1Value, 'edge1', this.handleEdge1Drag, this.handleEdge1DragStart)}
        {this.renderEdge('edge2', x2Value, y2Value, 'edge2', this.handleEdge2Drag, this.handleEdge2DragStart)}
        {this.renderEdge('edge3', x2Value, y1Value, 'edge3', this.handleEdge3Drag, this.handleEdge3DragStart)}
        {this.renderEdge('edge4', x1Value, y2Value, 'edge4', this.handleEdge4Drag, this.handleEdge4DragStart)}

        {line2Edge}
        {/* Mid line */}
        {middle?.show && this.renderLine('center', x1Value, (y1Value + y2Value) / 2, x2Value, (y1Value + y2Value) / 2)}

        {/* Mid-line Clickable Rectangles */}
        {this.renderClickableRect('top', (x1Value + x2Value) / 2, y1Value)}
        {this.renderClickableRect('bottom', (x1Value + x2Value) / 2, y2Value)}
        {this.renderClickableRect('left', x1Value, (y1Value + y2Value) / 2)}
        {this.renderClickableRect('right', x2Value, (y1Value + y2Value) / 2)}

        <HoverTextNearMouse
          show={hoverTextEnabled === true && hover === true}
          {...restHoverTextProps}
          text={selected ? hoverTextSelected : hoverTextUnselected}
        />
      </g>
    );
  }

  private renderLine = (refKey: string, x1: number, y1: number, x2: number, y2: number) => {
    const { strokeStyle, strokeWidth, strokeDasharray, selected, onDoubleClick, extendLeft, extendRight, middle } =
      this.props;
    const { hover, dragging } = this.state;

    if (dragging) {
      this.lineInteractive = 'react-financial-charts-grabbing-cursor';
    } else if (!dragging) {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    } else {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    }

    const finalStrokeStyle =
      (extendLeft && refKey === 'leftLine') || (extendRight && refKey === 'rightLine')
        ? makeTransparentColor(strokeStyle)
        : strokeStyle;

    const horzline = refKey === 'bottomLine' || refKey === 'topLine' || refKey === 'center';
    const isCenterLine = refKey === 'center';
    return (
      <InteractiveStraightLine
        key={refKey}
        ref={this.saveNodeType(refKey)}
        selected={selected || hover}
        onHover={this.handleHover}
        onUnHover={this.handleHover}
        x1Value={x1}
        y1Value={y1}
        x2Value={x2}
        y2Value={y2}
        type="LINE"
        strokeStyle={isCenterLine ? (middle?.strokeStyle ? middle?.strokeStyle : finalStrokeStyle) : finalStrokeStyle}
        strokeWidth={isCenterLine ? middle?.strokeWidth : strokeWidth}
        strokeDasharray={isCenterLine ? middle?.strokeDasharray : strokeDasharray}
        interactiveCursorClass={this.lineInteractive}
        onDragStart={this.handleLineDragStart}
        onDrag={this.handleLineDrag}
        onDragComplete={this.handleDragComplete}
        onMouseDown={this.handleShapeMouseDown}
        onDoubleClick={onDoubleClick}
        extendLeft={horzline ? extendLeft : false}
        extendRight={horzline ? extendRight : false}
      />
    );
  };

  private renderEdge = (
    refKey: string,
    cx: number,
    cy: number,
    anchorKey: string,
    onDrag: (e: React.MouseEvent, moreProps: MoreProps) => void,
    onDragStart: (anchor: string) => void,
  ) => {
    const { selected, edgeFill, edgeStrokeWidth, edgeInteractiveCursor, r, locked, edgeStroke, onDoubleClick } =
      this.props;

    const lockedR = locked ? r - 2 : r;
    const lockedEdgeStorke = locked ? '#436fe9' : edgeStroke;
    const lockedStrokeWidth = locked ? 1 : edgeStrokeWidth;
    const { hover } = this.state;

    return (
      <ClickableCircle
        key={refKey}
        ref={this.saveNodeType(refKey)}
        show={selected || hover}
        cx={cx}
        cy={cy}
        r={lockedR}
        fillStyle={edgeFill}
        strokeStyle={lockedEdgeStorke}
        strokeWidth={lockedStrokeWidth}
        interactiveCursorClass={edgeInteractiveCursor}
        onDragStart={() => onDragStart(anchorKey)}
        onDrag={onDrag}
        onDragComplete={this.handleDragComplete}
        onDoubleClick={onDoubleClick}
      />
    );
  };

  private renderClickableRect = (refKey: 'top' | 'bottom' | 'left' | 'right', cx: number, cy: number) => {
    const { selected, edgeFill, edgeStrokeWidth, r, locked, edgeStroke, onDoubleClick } = this.props;

    const lockedR = locked ? r - 2 : r;
    const lockedEdgeStorke = locked ? '#436fe9' : edgeStroke;
    const lockedStrokeWidth = locked ? 1 : edgeStrokeWidth;
    const { hover } = this.state;
    const interactiveEdgeCursorClass =
      refKey.includes('top') || refKey.includes('bottom')
        ? 'react-financial-charts-ns-resize-cursor'
        : 'react-financial-charts-ew-resize-cursor';
    return (
      <ClickableReactangle
        key={refKey}
        ref={this.saveNodeType(refKey)}
        show={selected || hover}
        cx={cx}
        cy={cy}
        r={lockedR}
        fillStyle={edgeFill}
        strokeStyle={lockedEdgeStorke}
        strokeWidth={lockedStrokeWidth}
        interactiveCursorClass={interactiveEdgeCursorClass}
        onDragStart={this.handleLineDragStart}
        onDrag={(e, moreProps) => this.handleSingleEdgeDrag(e, moreProps, refKey)}
        onDragComplete={this.handleDragComplete}
        onDoubleClick={onDoubleClick}
      />
    );
  };

  private readonly handleHover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { onHover, uuid, sharedState, isAnyShapeEnabled } = this.props;
    if (sharedState?.status === 'started' || isAnyShapeEnabled) return;
    const hovering = moreProps?.hovering;
    if (this.state.hover !== hovering) {
      this.setState({
        hover: hovering,
      });
      if (hovering && onHover) {
        onHover(uuid ?? null, moreProps);
      }
    }
  };

  private readonly handleShapeMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { onSelect, uuid, sharedState } = this.props;
    if (sharedState?.status === 'started') return;
    if (onSelect && isDefined(uuid)) {
      onSelect(e, uuid, moreProps);
    }
  };

  private readonly handleEdge3Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, x1Value, y2Value, locked, chartId } = this.props;
    if (chartId !== moreProps.chartId || locked) return;
    const [x2Value, y1Value] = getNewXY(moreProps);
    this.setState({ dragging: true });
    if (moreProps?.hovering) {
      onDrag(e, uuid, { x1Value, y1Value, x2Value, y2Value }, moreProps);
    }
  };

  private readonly handleEdge4Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, x2Value, y1Value, locked, chartId } = this.props;
    if (chartId !== moreProps.chartId || locked) return;
    const [x1Value, y2Value] = getNewXY(moreProps);
    this.setState({ dragging: true });
    if (moreProps?.hovering) {
      onDrag(e, uuid, { x1Value, y1Value, x2Value, y2Value }, moreProps);
    }
  };

  private readonly handleEdge2Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, x1Value, y1Value, locked, chartId } = this.props;
    if (chartId !== moreProps.chartId || locked) return;
    const [x2Value, y2Value] = getNewXY(moreProps);
    this.setState({ dragging: true });
    if (moreProps?.hovering) {
      onDrag(e, uuid, { x1Value, y1Value, x2Value, y2Value }, moreProps);
    }
  };

  private readonly handleEdge1Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, x2Value, y2Value, locked, chartId } = this.props;
    if (chartId !== moreProps.chartId || locked) return;
    const [x1Value, y1Value] = getNewXY(moreProps);
    this.setState({ dragging: true });
    if (moreProps?.hovering) {
      onDrag(e, uuid, { x1Value, y1Value, x2Value, y2Value }, moreProps);
    }
  };

  private readonly handleDragComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    this.setState({
      anchor: undefined,
      dragging: false,
    });
    const { onDragComplete, chartId } = this.props;
    if (onDragComplete === undefined || chartId !== moreProps.chartId) {
      return;
    }
    onDragComplete(e, moreProps);
  };

  private readonly handleEdge2DragStart = () => {
    const { locked } = this.props;
    if (locked) return;
    this.setState({
      anchor: 'edge1',
    });
  };

  private readonly handleEdge1DragStart = () => {
    const { locked } = this.props;
    if (locked) return;
    this.setState({
      anchor: 'edge2',
    });
  };

  private readonly handleEdge3DragStart = () => {
    const { locked } = this.props;
    if (locked) return;
    this.setState({
      anchor: 'edge3',
    });
  };

  private readonly handleEdge4DragStart = () => {
    const { locked } = this.props;
    if (locked) return;
    this.setState({
      anchor: 'edge4',
    });
  };

  private readonly handleLineDrag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, locked } = this.props;
    if (!this.dragStart || this.dragStart.chartId !== moreProps.chartId || locked) return;
    const { x1Value, y1Value, x2Value, y2Value } = this.dragStart;
    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
      hovering,
    } = moreProps;
    const startPos = moreProps.startPos!;
    const { mouseXY } = moreProps;
    const fullDataArr = (fullData ?? []) as unknown[];
    const x1 = xScale(x1Value);
    const y1 = yScale(y1Value);
    const x2 = xScale(x2Value);
    const y2 = yScale(y2Value);

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX1Value = getXValue(xScale, xAccessor, [x1 - dx, y1 - dy], fullDataArr);
    const newX2Value = getXValue(xScale, xAccessor, [x2 - dx, y2 - dy], fullDataArr);
    const newY1Value = yScale.invert(y1 - dy);
    const newY2Value = yScale.invert(y2 - dy);

    this.setState({ dragging: true });
    if (hovering) {
      onDrag(
        e,
        uuid,
        {
          x1Value: newX1Value,
          y1Value: newY1Value,
          x2Value: newX2Value,
          y2Value: newY2Value,
        },
        moreProps,
      );
    }
  };

  private readonly handleLineDragStart = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { x1Value, y1Value, x2Value, y2Value, locked } = this.props;
    if (locked) return;
    this.dragStart = {
      chartId: moreProps.chartId,
      x1Value,
      y1Value,
      x2Value,
      y2Value,
    };
  };

  private readonly handleSingleEdgeDrag = (
    e: React.MouseEvent,
    moreProps: MoreProps,
    edge: 'top' | 'bottom' | 'left' | 'right',
  ) => {
    const { uuid, onDrag, locked, chartId } = this.props;
    if (chartId !== moreProps.chartId || locked) return;

    const [xValue, yValue] = getNewXY(moreProps);

    if (!this.dragStart) return;
    const { x1Value, y1Value, x2Value, y2Value } = this.dragStart;

    const updatedValues = { x1Value, y1Value, x2Value, y2Value };

    switch (edge) {
      case 'top':
        updatedValues.y1Value = yValue;
        break;
      case 'bottom':
        updatedValues.y2Value = yValue;
        break;
      case 'left':
        updatedValues.x1Value = xValue;
        break;
      case 'right':
        updatedValues.x2Value = xValue;
        break;
    }

    this.setState({ dragging: true });
    if (moreProps?.hovering) {
      onDrag(e, uuid, updatedValues, moreProps);
    }
  };
}
